import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Pause, Play, Link2, Radio, Plug } from "lucide-react";

export default function Platforms() {
  const [platforms, setPlatforms] = useState([]);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    const { data } = await api.get("/platforms");
    setPlatforms(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.post(`/platforms/${p.id}/${p.paused ? "resume" : "pause"}`);
      toast.success(`${p.name} ${p.paused ? "resumed" : "paused"}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const checkBluesky = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/platforms/bluesky/status");
      if (data.connected) {
        toast.success(`Bluesky connected as @${data.handle} — ${data.followers} followers.`);
      } else {
        toast.error(data.error || "Bluesky connection failed.");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setChecking(false); }
  };

  return (
    <div data-testid="platforms-page">
      <div className="mb-8">
        <div className="overline">Platforms</div>
        <h1 className="font-display text-4xl mt-2">Six doors, one key.</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Bluesky is live. The other five remain mocked until you wire them — when you're ready,
          generate an app password for each one and the same pattern applies.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.map((p) => {
          const live = !!p.live;
          return (
            <div key={p.id} data-testid={`platform-${p.id}`}
                 className="elle-surface p-6 rounded-sm"
                 style={{ borderColor: live ? "var(--elle-moss)" : "var(--elle-border)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline flex items-center gap-2">
                    {live && <Radio size={12} style={{ color: "var(--elle-moss)" }}/>}
                    {p.name}
                  </div>
                  <div className="font-display text-2xl mt-1" style={{ color: "var(--elle-ink)" }}>
                    {p.handle ? `@${p.handle}` : "@elle_boggin"}
                  </div>
                </div>
                <span className="text-xs font-mono-elle px-2 py-1 rounded-sm"
                      style={{
                        background: p.paused
                          ? "rgba(139,42,42,0.15)"
                          : live
                          ? "rgba(61,90,64,0.2)"
                          : "rgba(154,160,166,0.1)",
                        color: p.paused
                          ? "var(--elle-oxblood-bright)"
                          : live
                          ? "var(--elle-moss)"
                          : "var(--elle-ink-dim)",
                      }}>
                  {p.paused ? "PAUSED" : live ? "LIVE" : "MOCKED"}
                </span>
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-4 text-sm font-mono-elle">
                <div>
                  <dt className="text-xs uppercase" style={{ color: "var(--elle-ink-faint)" }}>posts today</dt>
                  <dd>{p.posts_today}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase" style={{ color: "var(--elle-ink-faint)" }}>
                    {live ? "followers" : "engagement 24h"}
                  </dt>
                  <dd>{live ? (p.followers || 0) : p.engagement_24h}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs uppercase" style={{ color: "var(--elle-ink-faint)" }}>last post</dt>
                  <dd style={{ color: "var(--elle-ink-dim)" }}>
                    {p.last_post_at ? new Date(p.last_post_at).toLocaleString() : "—"}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex gap-2 flex-wrap">
                <Button onClick={() => toggle(p)}
                        data-testid={`toggle-${p.id}`}
                        className={p.paused ? "btn-amber rounded-sm" : "btn-ghost rounded-sm"}
                        variant={p.paused ? undefined : "outline"}>
                  {p.paused ? <><Play size={14} className="mr-2"/>Resume</> : <><Pause size={14} className="mr-2"/>Pause</>}
                </Button>
                {live ? (
                  <Button data-testid={`check-${p.id}`}
                          onClick={checkBluesky} disabled={checking}
                          variant="outline" className="btn-ghost rounded-sm">
                    <Plug size={14} className="mr-2"/> {checking ? "Checking…" : "Check connection"}
                  </Button>
                ) : (
                  <Button variant="outline" disabled className="btn-ghost rounded-sm">
                    <Link2 size={14} className="mr-2"/> Connect (later)
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
